import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { HashProvider } from '../../Hooks/UseHash';
import useMobileVh from '../../Hooks/UseMobileVh';
import useAccountDetails from '../../Hooks/UseAccountDetails';
import { ErrorHandler } from '../../Services/UserApi';
import Loading from '../../Abstracts/Components/Loading';
import { AccountDetails } from '../../Services/AccountService';

type Props = {
	hash: string;
};

export default function AccountDetails(props: Props) {
	const { hash } = props;
	useMobileVh();

	return (
		<HashProvider hash={hash}>
			<div className="container account-page">
				<div className="row">
					<div className="col-xs-12">
						<AccountDetailsContent />
					</div>
				</div>
			</div>
			<Toaster position="bottom-right" />
		</HashProvider>
	);
}

function AccountDetailsContent() {
	const { details, isLoading, saveDetails } = useAccountDetails();
	const [formState, setFormState] = useState<AccountDetails>();
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (details) {
			setFormState(details);
		}
	}, [details]);

	const states = useMemo(() => ['QLD', 'NSW', 'VIC', 'WA', 'NT', 'ACT', 'SA', 'TAS'], []);

	const onChange =
		(field: keyof AccountDetails) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			setFormState((prev) => (prev ? { ...prev, [field]: event.target.value } : prev));
		};

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!formState) {
			return;
		}

		setIsSaving(true);
		try {
			const updated = await saveDetails({
				MarketClientABN: formState.abn,
				MarketClientFacebookId: formState.facebook,
				MarketClientInstagramId: formState.instagram,
				MarketClientWebsiteUrl: formState.website,
				ContactPrimaryContactNumber: formState.contactNumber,
				ClientStreetAddress1: formState.streetAddress1,
				ClientStreetAddress2: formState.streetAddress2,
				ClientAddressCity: formState.suburb,
				ClientAddressState: formState.state,
				ClientAddressPostcode: formState.postcode,
			});
			setFormState(updated);
			toast.success('Details saved');
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading || !formState) {
		return <Loading message="Loading your account details" />;
	}

	return (
		<form onSubmit={onSubmit}>
			<div className="account-header">
				<div>
					<p className="eyebrow">Account</p>
					<h2>Stall details</h2>
					<p className="subhead">Update your contact, address, and social links.</p>
				</div>
				<div className="account-desktop-actions">
					<button type="submit" className="btn btn-primary account-btn" disabled={isSaving}>
						{isSaving ? (
							<>
								<i className="fas fa-spinner fa-pulse fa-fw" aria-hidden /> Saving...
							</>
						) : (
							'Save changes'
						)}
					</button>
				</div>
			</div>

			<div className="account-card-grid">
				<div className="account-card">
					<div className="account-card-header">Stall</div>
					<div className="account-field">
						<label htmlFor="MarketClientName">Stall name</label>
						<input
							id="MarketClientName"
							className="form-control"
							type="text"
							value={formState.stallName}
							disabled
						/>
						<span className="account-helper-text">To change your stall name, please send us an email.</span>
					</div>
					<div className="account-field">
						<label htmlFor="MarketClientABN">Business ABN</label>
						<input
							id="MarketClientABN"
							className="form-control"
							type="text"
							value={formState.abn}
							onChange={onChange('abn')}
							placeholder="00 000 000 000"
						/>
						<span className="account-helper-text">A valid ABN is not required to trade.</span>
					</div>
				</div>

				<div className="account-card">
					<div className="account-card-header">Social media</div>
					<div className="account-field">
						<label htmlFor="MarketClientFacebookId">Facebook</label>
						<input
							id="MarketClientFacebookId"
							className="form-control"
							type="text"
							value={formState.facebook}
							onChange={onChange('facebook')}
							placeholder="@mybusiness"
						/>
					</div>
					<div className="account-field">
						<label htmlFor="MarketClientInstagramId">Instagram</label>
						<input
							id="MarketClientInstagramId"
							className="form-control"
							type="text"
							value={formState.instagram}
							onChange={onChange('instagram')}
							placeholder="@mybusiness"
						/>
					</div>
					<div className="account-field">
						<label htmlFor="MarketClientWebsiteUrl">Website</label>
						<input
							id="MarketClientWebsiteUrl"
							className="form-control"
							type="text"
							value={formState.website}
							onChange={onChange('website')}
							placeholder="https://"
						/>
					</div>
				</div>

				<div className="account-card">
					<div className="account-card-header">Contact</div>
					<div className="account-field">
						<label htmlFor="ContactEmailAddress">Email address</label>
						<input
							id="ContactEmailAddress"
							className="form-control"
							type="email"
							value={formState.contactEmail}
							disabled
						/>
						<span className="account-helper-text">
							To change your email address, please give our office a call.
						</span>
					</div>
					<div className="account-field">
						<label htmlFor="ContactPrimaryContactNumber">Contact number</label>
						<input
							id="ContactPrimaryContactNumber"
							className="form-control"
							type="text"
							value={formState.contactNumber}
							onChange={onChange('contactNumber')}
							placeholder="04xx xxx xxx"
						/>
					</div>
				</div>

				<div className="account-card">
					<div className="account-card-header">Home or business address</div>
					<div className="account-field">
						<label htmlFor="ClientStreetAddress1">Street address 1</label>
						<input
							id="ClientStreetAddress1"
							className="form-control"
							type="text"
							value={formState.streetAddress1}
							onChange={onChange('streetAddress1')}
						/>
					</div>
					<div className="account-field">
						<label htmlFor="ClientStreetAddress2">Street address 2</label>
						<input
							id="ClientStreetAddress2"
							className="form-control"
							type="text"
							value={formState.streetAddress2}
							onChange={onChange('streetAddress2')}
						/>
					</div>
					<div className="account-field">
						<label htmlFor="ClientAddressCity">Suburb</label>
						<input
							id="ClientAddressCity"
							className="form-control"
							type="text"
							value={formState.suburb}
							onChange={onChange('suburb')}
						/>
					</div>
					<div className="account-address-row">
						<div className="account-field account-field--narrow">
							<label htmlFor="ClientAddressState">State</label>
							<select
								id="ClientAddressState"
								className="form-control"
								value={formState.state}
								onChange={onChange('state')}
							>
								{states.map((state) => (
									<option key={state} value={state}>
										{state}
									</option>
								))}
							</select>
						</div>
						<div className="account-field account-field--narrow">
							<label htmlFor="ClientAddressPostcode">Postcode</label>
							<input
								id="ClientAddressPostcode"
								className="form-control"
								type="text"
								value={formState.postcode}
								onChange={onChange('postcode')}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="account-sticky-actions">
				<button type="submit" className="btn btn-primary account-btn" disabled={isSaving}>
					{isSaving ? (
						<>
							<i className="fas fa-spinner fa-pulse fa-fw" aria-hidden /> Saving...
						</>
					) : (
						'Save changes'
					)}
				</button>
			</div>
		</form>
	);
}
